<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import type { VTextField } from "vuetify/components";
import {
	maskInteger,
	unmaskInteger,
} from "@/modules/campaign_planner/utils/maskingUtils";
import { availabilityColor } from "./availabilityColor";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const props = defineProps<{
	flight: FlightDO;
	screens?: ScreenDO[];
}>();

const visible = computed(
	() =>
		props.flight.goal_name_clean === "reach" &&
		props.flight.type_of_buy === "goal_impressions"
);

const formatCompactNumber = (value: number) =>
	new Intl.NumberFormat("en-EN", {
		notation: "compact",
		maximumFractionDigits: 1,
	}).format(value);

const totalMaxAvailableImpressions = computed(() =>
	(props.screens ?? []).reduce(
		(sum, screen) =>
			sum + (screen.availability?.max_available_impressions ?? 0),
		0
	)
);

const goalTarget = computed(() => Number(props.flight.goal_target ?? 0));

const progress = computed(() => {
	if (totalMaxAvailableImpressions.value <= 0) return 0;
	return Math.min(
		100,
		(goalTarget.value / totalMaxAvailableImpressions.value) * 100
	);
});

const progressColor = computed(() => availabilityColor(100 - progress.value));

const editingGoalTarget = ref(false);
const goalTargetField = ref<InstanceType<typeof VTextField>>();

const goalTargetInput = computed({
	get() {
		return maskInteger(props.flight.goal_target as number | null);
	},
	set(v: string) {
		props.flight.goal_target = unmaskInteger(v);
	},
});

const goalTargetRules = [
	() =>
		(props.flight.goal_target != null &&
			(props.flight.goal_target as number) > 0) ||
		"Must be greater than 0",
];

const startEditingGoalTarget = async () => {
	editingGoalTarget.value = true;
	await nextTick();
	goalTargetField.value?.focus();
};

const stopEditingGoalTarget = () => {
	editingGoalTarget.value = false;
};
</script>

<template>
	<v-fade-transition>
		<v-card v-if="visible" class="flight-goal-card pa-4">
			<div class="text-subtitle-2 font-weight-bold mb-3">
				Impression Availability
			</div>

			<v-progress-linear
				:model-value="progress"
				:color="progressColor"
				rounded
				height="8"
			/>

			<div class="d-flex justify-space-between align-center mt-3">
				<div>
					<div class="text-caption text-medium-emphasis">Requested</div>
					<v-text-field
						v-if="editingGoalTarget"
						ref="goalTargetField"
						v-model="goalTargetInput"
						type="text"
						inputmode="decimal"
						density="compact"
						hide-details="auto"
						style="width: 150px"
						:rules="goalTargetRules"
						@blur="stopEditingGoalTarget"
						@keyup.enter="stopEditingGoalTarget"
					/>
					<div
						v-else
						class="d-flex align-center ga-1 font-weight-bold goal-target-value"
						:style="{ color: progressColor }"
						@click="startEditingGoalTarget"
					>
						<v-icon icon="fa-regular fa-eye" size="small" />
						{{ formatCompactNumber(goalTarget) }}
					</div>
				</div>

				<div class="text-right">
					<div
						class="text-caption text-medium-emphasis d-flex align-center ga-1"
					>
						Total Available
						<div
							style="margin-top: -4px"
							v-tooltip="
								`PQA wont be able to optimize a solution, but will return a price-quote for the ${formatCompactNumber(
									totalMaxAvailableImpressions
								)} available impressions.`
							"
							v-if="progress >= 100"
						>
							<v-icon icon="fa-regular fa-triangle-exclamation" size="small" />
						</div>
					</div>
					<div class="font-weight-bold">
						/ {{ formatCompactNumber(totalMaxAvailableImpressions) }}
					</div>
				</div>
			</div>
		</v-card>
	</v-fade-transition>
</template>

<style scoped>
.flight-goal-card {
	position: absolute;
	top: 12px;
	left: 12px;
	z-index: 1;
	min-width: 320px;
}

.goal-target-value {
	cursor: pointer;
}
</style>
